import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { subDays } from "date-fns";
import { DEMO_ACCOUNT, useAuth } from "./AuthContext";
import { getBookableDays, toDateKey, toDateTime } from "../utils/slots";
import type { Appointment, NewAppointmentInput } from "../types/booking";
type Store = Record<string, Appointment[]>;
interface BookingsContextValue {
  appointments: Appointment[];
  addAppointment: (input: NewAppointmentInput) => Promise<Appointment>;
  cancelAppointment: (id: string) => Promise<void>;
  isSlotTaken: (dateKey: string, time: string) => boolean;
}
const STORE_KEY = "rdk-appointments";
const BookingsContext = createContext<BookingsContextValue | null>(null);
export function BookingsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [store, setStore] = useState<Store>(readStore);
  const persist = useCallback((next: Store) => {
    setStore(next);
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
  }, []);
  const appointments = useMemo(() => {
    if (!user) return [];
    return [...(store[user.email] ?? [])].sort((a, b) => toDateTime(a.dateKey, a.time).getTime() - toDateTime(b.dateKey, b.time).getTime());
  }, [store, user]);
  const isSlotTaken = useCallback((dateKey: string, time: string) => Object.values(store).some(list => list.some(a => a.status === "confirmed" && a.dateKey === dateKey && a.time === time)), [store]);
  const addAppointment = useCallback(async (input: NewAppointmentInput) => {
    await wait(800);
    if (!user) throw new Error("Veuillez vous connecter pour confirmer votre rendez-vous.");
    if (isSlotTaken(input.dateKey, input.time)) {
      throw new Error("Ce créneau vient d'être réservé. Merci d'en choisir un autre.");
    }
    const appointment: Appointment = {
      ...input,
      id: `rdv-${Date.now()}`,
      status: "confirmed",
      createdAt: new Date().toISOString()
    };
    persist({
      ...store,
      [user.email]: [...(store[user.email] ?? []), appointment]
    });
    return appointment;
  }, [user, store, isSlotTaken, persist]);
  const cancelAppointment = useCallback(async (id: string) => {
    await wait(500);
    if (!user) return;
    const list = (store[user.email] ?? []).map(a => a.id === id ? {
      ...a,
      status: "cancelled" as const
    } : a);
    persist({
      ...store,
      [user.email]: list
    });
  }, [user, store, persist]);
  const value = useMemo(() => ({
    appointments,
    addAppointment,
    cancelAppointment,
    isSlotTaken
  }), [appointments, addAppointment, cancelAppointment, isSlotTaken]);
  return <BookingsContext.Provider value={value} data-id="element-328" data-mp="298ai7cy2nhb0">{children}</BookingsContext.Provider>;
}
export function useBookings(): BookingsContextValue {
  const ctx = useContext(BookingsContext);
  if (!ctx) throw new Error("useBookings must be used within BookingsProvider");
  return ctx;
}
function readStore(): Store {
  let store: Store = {};
  try {
    const raw = localStorage.getItem(STORE_KEY);
    store = raw ? JSON.parse(raw) as Store : {};
  } catch {
    store = {};
  }
  if (!store[DEMO_ACCOUNT.email]) {
    const upcoming = getBookableDays(6)[4];
    store[DEMO_ACCOUNT.email] = [{
      id: "rdv-demo-past",
      serviceId: "bilan",
      dateKey: toDateKey(subDays(new Date(), 18)),
      time: "09:00",
      status: "confirmed",
      createdAt: subDays(new Date(), 25).toISOString()
    }, {
      id: "rdv-demo-next",
      serviceId: "reeducation",
      dateKey: toDateKey(upcoming),
      time: "10:30",
      status: "confirmed",
      createdAt: new Date().toISOString()
    }];
  }
  return store;
}
function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}